import { useApolloClient } from '@apollo/client';
import { ExitToApp, Home } from '@mui/icons-material';
import { BookmarkAdded } from '@mui/icons-material';
import {
  Divider,
  Link as MuiLink,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { isShibUser } from '@thro-helpers/users';
import { User } from '@thro-types/users';

import clsx from 'clsx';
import { t } from 'i18next';
import { Fragment, ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { version } from 'app/../../package.json';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import menuItems from './MenuItems';
import Section from './Section';

interface Props {
  user?: User;
  mobile?: boolean;
  handleClick?: () => void;
}

export default function Menu({ mobile, handleClick }: Props): ReactElement {
  const { pathname } = useLocation();
  const client = useApolloClient();
  const { user, permissions: userPermission } = useAuthContext();
  if (!user || !userPermission) return <></>;

  const aclHelper = new ACLHelper(user);

  const handleLogout = async () => {
    const token = localStorage.getItem('jwt-token');
    localStorage.removeItem('jwt-token');
    localStorage.removeItem('refresh-token');
    await client.clearStore();
    client.cache.gc();
    Sentry.setUser(null);
    if (isShibUser(user) && !aclHelper.isTestUser()) {
      window.location.href = `${import.meta.env.VITE_SHIB_LOGOUT_URL}?token=${token}`;
    } else {
      window.location.href = '/login';
    }
  };

  const menuItemsList = menuItems(user, userPermission).filter(m => m.show());
  return (
    <div className="navigation">
      <List dense>
        <Link to="/" onClick={handleClick}>
          <ListItemButton className={clsx('navItem', { active: pathname === '/' })}>
            <ListItemIcon className="icon">
              <Home />
            </ListItemIcon>
            <ListItemText primary={t(translations.layout.menu.dashboard())} />
          </ListItemButton>
        </Link>
        <Link to="/watchlist" onClick={handleClick}>
          <ListItemButton className={clsx('navItem', { active: pathname === '/watchlist' })}>
            <ListItemIcon className="icon">
              <BookmarkAdded />
            </ListItemIcon>
            <ListItemText primary={t(translations.layout.menu.watchlist())} />
          </ListItemButton>
        </Link>
        {mobile && (
          <MuiLink onClick={() => handleLogout()}>
            <ListItemButton>
              {/* </ListItem> */}
              <ListItemIcon className="icon">
                <ExitToApp />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </MuiLink>
        )}
      </List>
      <Divider variant="middle" className="divider" />
      {menuItemsList.map((m, idx) => {
        return (
          <Fragment key={idx}>
            <Section menu={m} />
            {idx + 1 < menuItemsList.length && <Divider variant="middle" className="divider" />}
          </Fragment>
        );
      })}
      {aclHelper.isSuperUser() && (
        <Typography
          textAlign={'center'}
          margin={1}
          variant="body2"
          sx={{
            whiteSpace: 'normal',
            maxWidth: '100%'
          }}
        >
          {version}
        </Typography>
      )}
    </div>
  );
}
